/**
 * MoneyPage — `/app/money`. "Where your money leaks."
 *
 * On load: GET /me/money/insights with the saved per-user token. Shows two big
 * friendly cards (total spent · money leaking), then the insight list (title,
 * message, "save ₪X"). If empty, a "See where your money goes" button POSTs a
 * realistic sample to /me/money/report so the screen is alive and demonstrates
 * the value immediately.
 *
 * Honest note: connecting a real bank needs a connector and is marked "coming
 * soon". Reporting-your-data (this seed, or a future connector) works today —
 * the numbers you see are computed from exactly that data.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, ShoppingBag, RefreshCw, TrendingDown } from 'lucide-react';

import {
  getMonitorToken,
  fetchMoneyInsights,
  reportMoney,
  type MoneyTransaction,
  type MoneyInsight,
} from '@/pages/monitor/monitorClient';
import { c } from './theme';
import { Card, ComingPill, ErrorCard, EmptyCard } from './ui';

// A realistic seed so the first open demonstrates the value immediately:
// ~₪847 of food delivery across several orders, plus 6 subscriptions — some of
// them not charged since early in the year, so the analysis flags them unused.
const SAMPLE: MoneyTransaction[] = [
  { merchant: 'Wolt', category: 'Food delivery', amount_cents: 30000, occurred_iso: '2026-06-03T19:00:00Z' },
  { merchant: 'Wolt', category: 'Food delivery', amount_cents: 30000, occurred_iso: '2026-06-11T20:00:00Z' },
  { merchant: 'Wolt', category: 'Food delivery', amount_cents: 24700, occurred_iso: '2026-06-19T19:30:00Z' },
  { merchant: 'Netflix', category: 'Streaming', amount_cents: 5490, occurred_iso: '2026-06-01T00:00:00Z', recurring: true },
  // Forgotten subscriptions — last charged in winter, never since.
  { merchant: 'Disney+', category: 'Streaming', amount_cents: 2890, occurred_iso: '2026-01-05T00:00:00Z' },
  { merchant: 'Disney+', category: 'Streaming', amount_cents: 2890, occurred_iso: '2026-02-05T00:00:00Z' },
  { merchant: 'Cloud Backup', category: 'Apps', amount_cents: 1990, occurred_iso: '2026-01-09T00:00:00Z' },
  { merchant: 'Cloud Backup', category: 'Apps', amount_cents: 1990, occurred_iso: '2026-02-09T00:00:00Z' },
  { merchant: 'Gym', category: 'Fitness', amount_cents: 12900, occurred_iso: '2026-01-02T00:00:00Z' },
  { merchant: 'Gym', category: 'Fitness', amount_cents: 12900, occurred_iso: '2026-02-02T00:00:00Z' },
  { merchant: 'Meditation App', category: 'Apps', amount_cents: 4990, occurred_iso: '2026-01-15T00:00:00Z' },
  { merchant: 'Meditation App', category: 'Apps', amount_cents: 4990, occurred_iso: '2026-02-15T00:00:00Z' },
  { merchant: 'News+', category: 'News', amount_cents: 3490, occurred_iso: '2026-01-20T00:00:00Z' },
  { merchant: 'News+', category: 'News', amount_cents: 3490, occurred_iso: '2026-02-20T00:00:00Z' },
];

/** ₪ from i64 cents — whole shekels, no decimals (friendly + big). */
function shekels(cents: number): string {
  return `₪${Math.round(cents / 100).toLocaleString()}`;
}

export function MoneyPage() {
  const qc = useQueryClient();
  const token = getMonitorToken();

  const ins = useQuery({
    queryKey: ['app', 'money', token],
    queryFn: () => fetchMoneyInsights(token!),
    enabled: Boolean(token),
  });

  const seed = useMutation({
    mutationFn: () => reportMoney(token!, SAMPLE),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app', 'money', token] }),
  });

  const data = ins.data;
  const insights = data?.insights ?? [];
  const hasData = (data?.summary.total_spent_cents ?? 0) > 0 || insights.length > 0;

  return (
    <div style={{ padding: '20px 18px 8px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '8px 0 4px' }}>Where your money leaks</h1>
      <p style={{ fontSize: 14, color: c.inkMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
        Small drips add up. Here's where to plug them.
      </p>

      {ins.isPending && <SkeletonHeadline />}

      {ins.isError && (
        <ErrorCard onRetry={() => ins.refetch()}>We couldn't load your money just now.</ErrorCard>
      )}

      {!ins.isPending && !ins.isError && data && (
        <>
          {hasData && (
            <Headline
              spent={data.summary.total_spent_cents}
              wasted={data.summary.wasted_cents}
            />
          )}

          {!hasData ? (
            <EmptyCard
              title="See where your money goes"
              body="Run a quick look and we'll show you the spending that adds up — and the subscriptions you forgot."
              actionLabel={seed.isPending ? 'Looking…' : 'See where your money goes'}
              onAction={() => seed.mutate()}
              disabled={seed.isPending}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
              {insights.map((i, idx) => (
                <InsightCard key={`${i.title}-${idx}`} insight={i} />
              ))}
            </div>
          )}

          {seed.isError && (
            <p style={{ fontSize: 13, color: c.danger, marginTop: 12 }}>
              That didn't go through — tap to try again.
            </p>
          )}
        </>
      )}

      <BankConnect />
    </div>
  );
}

// ── Headline numbers ─────────────────────────────────────────────────────────

function Headline({ spent, wasted }: { spent: number; wasted: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
      <BigStat n={shekels(spent)} label="spent so far" fg={c.ink} bg={c.canvas} rim={c.rim} />
      <BigStat
        n={shekels(wasted)}
        label="leaking each month"
        fg={c.danger}
        bg={c.dangerSoft}
        rim={c.dangerRim}
      />
    </div>
  );
}

function BigStat({
  n,
  label,
  fg,
  bg,
  rim,
}: {
  n: string;
  label: string;
  fg: string;
  bg: string;
  rim: string;
}) {
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${rim}`,
        borderRadius: 16,
        padding: '18px 14px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 30, fontWeight: 800, color: fg, lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: c.inkMuted, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function SkeletonHeadline() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{ height: 92, borderRadius: 16, background: c.canvas, border: `1px solid ${c.rim}` }}
        />
      ))}
    </div>
  );
}

// ── Insight card ─────────────────────────────────────────────────────────────

function insightIcon(title: string) {
  const t = title.toLowerCase();
  if (t.includes('subscription') || t.includes('paying for')) return RefreshCw;
  if (t.includes('food') || t.includes('delivery')) return ShoppingBag;
  return TrendingDown;
}

function InsightCard({ insight }: { insight: MoneyInsight }) {
  const Icon = insightIcon(insight.title);
  const saves = insight.saving_cents > 0;
  return (
    <div
      style={{
        background: saves ? c.goodSoft : c.surface,
        border: `1px solid ${saves ? c.goodRim : c.rim}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        gap: 14,
        alignItems: 'center',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: saves ? c.good : c.surface,
          color: saves ? '#ffffff' : c.accent,
          border: saves ? 'none' : `1px solid ${c.rim}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{insight.title}</div>
        <div style={{ fontSize: 13, color: c.inkMuted, marginTop: 3, lineHeight: 1.5 }}>
          {insight.message}
        </div>
      </div>

      {saves && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 800,
            color: c.good,
            whiteSpace: 'nowrap',
          }}
        >
          save {shekels(insight.saving_cents)}
        </span>
      )}
    </div>
  );
}

// ── Bank connect (honestly coming soon) ──────────────────────────────────────

function BankConnect() {
  return (
    <div style={{ marginTop: 22 }}>
      <Card
        tone="accent"
        icon={<Landmark size={22} />}
        title="Connect your bank"
        body="Link your account and Monitor finds the leaks automatically — read-only, always."
        right={<ComingPill />}
      />
      <p style={{ fontSize: 12, color: c.inkSubtle, margin: '12px 2px 0', lineHeight: 1.5 }}>
        Your data stays yours. Until bank connect is live, the numbers above come from the sample
        we looked at — your real numbers appear the moment your bank's linked.
      </p>
    </div>
  );
}

export default MoneyPage;
